import { useUserWallets } from '@dynamic-labs/sdk-react-core';
import { CheckCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useProjectContractSync } from 'src/modules/projects/hooks/useProjectContractSync';
import { StellarDeployStatus, useStellarDeployProject } from 'src/modules/projects/hooks/useStellarDeployProject';

import {
  getStellarExplorerContractUrl,
  getStellarExplorerTxUrl,
  getStellarNetworkConfig,
  StellarChainId,
} from '@/config/stellar';
import { Button, Link as DSLink, Spinner, Text } from '@/design-system/components/ui';

import { Callout } from './callouts/Callout';

const STELLAR_STATUS_MESSAGES: Record<StellarDeployStatus, string> = {
  idle: 'Ready to initialize',
  loading: 'Preparing transaction...',
  signing: 'Please confirm in your wallet...',
  confirming: 'Confirming transaction...',
  success: 'Project contract created!',
  error: 'Deployment failed',
};

interface StellarDeploySectionProps {
  chainId: StellarChainId;
  projectId: string;
  /** Fired once the backend has linked the created contract to the project. */
  onSuccess: () => void;
  onClose: () => void;
}

export const StellarDeploySection = ({ chainId, projectId, onSuccess, onClose }: StellarDeploySectionProps) => {
  const networkConfig = getStellarNetworkConfig(chainId);
  const userWallets = useUserWallets();
  const { deploy, status, hash, contractId, error, reset, isLoading } = useStellarDeployProject();

  // The contract address is linked to the project by Fuul ops for now, so this can time out
  const { status: syncStatus } = useProjectContractSync({
    projectId,
    enabled: status === 'success',
    onSynced: onSuccess,
  });

  const stellarWallet = useMemo(() => {
    return userWallets?.find((w) => w.chain === 'STELLAR') ?? null;
  }, [userWallets]);

  if (!stellarWallet) {
    return (
      <Callout
        type="warning"
        title="Stellar Wallet Required"
        body="Connected user is not a Stellar wallet. Invite a Stellar address to the team and login with that address to initialize the project."
      />
    );
  }

  const isSyncing = syncStatus === 'syncing';
  const isSyncTimedOut = syncStatus === 'timed-out';

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Text variant="body-md" color="muted">
        Initialize your project on {networkConfig.name}. Make sure your wallet is on {networkConfig.name} and holds
        enough {networkConfig.nativeCurrencySymbol} for the transaction fee.
      </Text>

      <div className="tw:flex tw:items-center tw:gap-2">
        {(isLoading || isSyncing) && <Spinner />}
        {status === 'success' && !isSyncing && <CheckCircle size={20} color="green" />}
        <Text variant="body-md">{STELLAR_STATUS_MESSAGES[status]}</Text>
      </div>

      {hash && (
        <DSLink href={getStellarExplorerTxUrl(hash, chainId)} external>
          View transaction on {networkConfig.explorerName}
        </DSLink>
      )}

      {status === 'confirming' && (
        <Callout
          type="info"
          title="Transaction in progress"
          body="This may take a few minutes. Please don't close this window or try to deploy again."
        />
      )}

      {/* Success: full contract ID, ops need it verbatim to link the project */}
      {contractId && status === 'success' && (
        <Callout
          type="success"
          title="Project contract ID"
          body={
            <span className="tw:flex tw:flex-col tw:gap-1">
              <span className="tw:break-all tw:font-mono">{contractId}</span>
              <DSLink href={getStellarExplorerContractUrl(contractId, chainId)} external>
                View contract on {networkConfig.explorerName}
              </DSLink>
            </span>
          }
        />
      )}

      {isSyncing && (
        <Callout
          type="info"
          title="Linking the contract to your project"
          body="The Fuul team links Stellar contracts to projects manually, so this can take a while. You can close this window; the dashboard updates once the contract is linked."
        />
      )}

      {isSyncTimedOut && (
        <Callout
          type="warning"
          title="Your contract is not linked yet"
          body="The contract was created on-chain but is not linked to your project yet. Contact the Fuul team with the contract ID above to finish the setup. Do not initialize again."
        />
      )}

      {error && <Callout type="error" title="Failed" body={error.message} />}

      <div className="tw:flex tw:flex-row tw:gap-2">
        <Button
          variant="primary"
          onClick={status === 'success' ? onClose : () => deploy({ chainId, projectId })}
          disabled={isLoading}
        >
          {status === 'success' ? 'Close' : isLoading ? 'Processing...' : `Initialize on ${networkConfig.name}`}
        </Button>
        {error && (
          <Button variant="outline" onClick={reset}>
            Retry
          </Button>
        )}
      </div>
    </div>
  );
};
